import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useActivateIdentity,
  ActivationError,
} from '@/hooks/useProvisionEnterpriseMember';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * /enterprise/activate (#615) — enterprise first-login activation landing page.
 *
 * A manually-added member clicks the first-login link, which (after the
 * magic-link / OAuth round-trip lands them authenticated) routes here with a
 * `?partner=<id>` query param. Once a session exists, this page activates the
 * member's own enterprise identity exactly once via enterprise-activate-identity
 * (the server uses caller.id, so the member only activates themselves).
 *
 * CRITICAL: the member is a REGULAR member, NOT an enterprise admin — so this
 * route is mounted as a plain authenticated route under DashboardLayout, OUTSIDE
 * the admin-gated EnterpriseLayout. Any authenticated user can load it.
 */
export function EnterpriseActivate(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const partnerId = searchParams.get('partner') ?? '';

  const activate = useActivateIdentity();
  const { mutate } = activate;
  // Guard so we fire the mutation exactly once, even across re-renders.
  const firedRef = useRef(false);

  useEffect(() => {
    if (loading || !user || !partnerId) return;
    if (firedRef.current) return;
    firedRef.current = true;
    mutate({ partner_id: partnerId });
  }, [loading, user, partnerId, mutate]);

  // Missing partner param → nothing to activate; show an error state.
  const missingPartner = !loading && !!user && !partnerId;

  const errCode =
    activate.error instanceof ActivationError ? activate.error.code : undefined;
  const errMessage =
    activate.error instanceof Error ? activate.error.message : 'Something went wrong.';

  let body: JSX.Element;
  if (missingPartner) {
    body = (
      <ErrorState
        title="This link is missing information"
        message="We couldn't tell which enterprise to activate. Please use the link from your invitation email, or contact your administrator."
      />
    );
  } else if (loading || !user || activate.isPending || (activate.isIdle && !!partnerId)) {
    body = (
      <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">Activating your account…</p>
      </div>
    );
  } else if (activate.isSuccess) {
    body = (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        <p className="text-sm text-muted-foreground">
          You&rsquo;re all set &mdash; your account is active.
        </p>
        <Button onClick={() => navigate('/')}>Continue to BuildIt</Button>
      </div>
    );
  } else if (errCode === 'identity_revoked') {
    body = (
      <ErrorState
        title="Your access has been revoked"
        message={errMessage}
      />
    );
  } else {
    body = <ErrorState title="We couldn't activate your account" message={errMessage} />;
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">Activate your enterprise account</CardTitle>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </div>
  );
}

function ErrorState({ title, message }: { title: string; message: string }): JSX.Element {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <ShieldAlert className="h-10 w-10 text-destructive" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
      <Button variant="outline" onClick={() => navigate('/')}>
        Back to BuildIt
      </Button>
    </div>
  );
}
